import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COMPETITIONS_COLLECTION = 'competitions';

// Create Competition
export const createCompetition = async (competitionData) => {
    try {
        if (!competitionData.startupIds || competitionData.startupIds.length === 0) {
            throw new Error('At least one startup must be selected');
        }

        // ✅ OPTIONAL: Validate competition link
        if (
            competitionData.competitionLink &&
            !competitionData.competitionLink.startsWith('http')
        ) {
            throw new Error('Competition link must be a valid URL');
        }

        const docRef = await addDoc(collection(db, COMPETITIONS_COLLECTION), {
            ...competitionData,
            teams: [],
            createdAt: new Date().toISOString(),
            updatedAt: serverTimestamp(),
        });

        // console.log('✅ Competition created:', docRef.id);
        return { id: docRef.id, ...competitionData, teams: [] };
    } catch (error) {
        console.error('❌ Error creating competition:', error);
        throw new Error(`Failed to create competition: ${error.message}`);
    }
};


// Get All Competitions
export const getCompetitions = async (startupId = null) => {
    try {
        let q;
        if (startupId) {
            q = query(
                collection(db, COMPETITIONS_COLLECTION),
                where('startupIds', 'array-contains', startupId)
            );
        } else {
            q = query(collection(db, COMPETITIONS_COLLECTION));
        }

        const querySnapshot = await getDocs(q);
        const competitions = [];

        querySnapshot.forEach((doc) => {
            competitions.push({ id: doc.id, ...doc.data() });
        });

        // console.log('✅ Competitions fetched:', competitions.length);
        return competitions;
    } catch (error) {
        console.error('❌ Error fetching competitions:', error);
        throw new Error(`Failed to fetch competitions: ${error.message}`);
    }
};

// Get Competition By ID
export const getCompetitionById = async (competitionId) => {
    try {
        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error('Competition not found');
        }

        // console.log('✅ Competition fetched:', competitionId);
        return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
        console.error('❌ Error fetching competition:', error);
        throw new Error(`Failed to fetch competition: ${error.message}`);
    }
};

// Update Competition
export const updateCompetition = async (competitionId, updates) => {
    try {
        if (updates.startupIds && updates.startupIds.length === 0) {
            throw new Error('At least one startup must be selected');
        }

        // ✅ OPTIONAL: Validate competition link on update
        if (
            updates.competitionLink &&
            !updates.competitionLink.startsWith('http')
        ) {
            throw new Error('Competition link must be a valid URL');
        }

        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });

        // console.log('✅ Competition updated:', competitionId);
        return { id: competitionId, ...updates };
    } catch (error) {
        console.error('❌ Error updating competition:', error);
        throw new Error(`Failed to update competition: ${error.message}`);
    }
};


// Delete Competition
export const deleteCompetition = async (competitionId) => {
    try {
        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        await deleteDoc(docRef);

        // console.log('✅ Competition deleted:', competitionId);
    } catch (error) {
        console.error('❌ Error deleting competition:', error);
        throw new Error(`Failed to delete competition: ${error.message}`);
    }
};

// ==================== TEAM OPERATIONS ====================

// Add Team to Competition
export const addCompetitionTeam = async (competitionId, teamData) => {
    try {
        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        const team = {
            id: Date.now().toString(),
            ...teamData,
            createdAt: new Date().toISOString(),

        };

        await updateDoc(docRef, {
            teams: arrayUnion(team),

        });

        // console.log('✅ Team added:', competitionId);
        return team;
    } catch (error) {
        console.error('❌ Error adding team:', error);
        throw new Error(`Failed to add team: ${error.message}`);
    }
};

// Update Team in Competition
export const updateCompetitionTeam = async (competitionId, teamId, updates) => {
    try {
        const comp = await getCompetitionById(competitionId);
        const updatedTeams = comp.teams.map(team =>
            team.id === teamId ? { ...team, ...updates, updatedAt: serverTimestamp() } : team
        );

        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        await updateDoc(docRef, {
            teams: updatedTeams,
            updatedAt: serverTimestamp(),
        });

        // console.log('✅ Team updated:', teamId);
        return updates;
    } catch (error) {
        console.error('❌ Error updating team:', error);
        throw new Error(`Failed to update team: ${error.message}`);
    }
};

// Delete Team from Competition
export const deleteCompetitionTeam = async (competitionId, teamId) => {
    try {
        const comp = await getCompetitionById(competitionId);
        const teamToRemove = comp.teams.find(t => t.id === teamId);

        const docRef = doc(db, COMPETITIONS_COLLECTION, competitionId);
        await updateDoc(docRef, {
            teams: arrayRemove(teamToRemove),
            updatedAt: serverTimestamp(),
        });

        // console.log('✅ Team deleted:', teamId);
    } catch (error) {
        console.error('❌ Error deleting team:', error);
        throw new Error(`Failed to delete team: ${error.message}`);
    }
};

export default {
    createCompetition,
    getCompetitions,
    getCompetitionById,
    updateCompetition,
    deleteCompetition,
    addCompetitionTeam,
    updateCompetitionTeam,
    deleteCompetitionTeam,
};
